import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wifi, 
  Server, 
  Database, 
  Zap, 
  CheckCircle, 
  ArrowRight,
  Users,
  TrendingUp,
  Target,
  BarChart3,
  Shield,
  Settings,
  Play,
  Download,
  Star,
  Clock,
  DollarSign,
  Building2,
  Cpu,
  Lock,
  MessageSquare,
  Calendar,
  MapPin,
  Briefcase,
  Mail,
  Phone,
  ExternalLink,
  Lightbulb,
  PieChart,
  Activity,
  Globe,
  Monitor,
  Smartphone,
  Globe2,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plus,
  Bot,
  Network,
  Layers,
  Code,
  GitBranch,
  GitCommit,
  GitPullRequest,
  GitMerge,
  GitCompare,
  GitFork,
  Signal,
  Radio,
  Bluetooth,
  WifiOff,
  SignalHigh,
  SignalMedium,
  SignalLow,
  SignalZero,
  Router,
  HardDrive,
  MemoryStick,
  CpuIcon,
  Gauge,
  Thermometer,
  Battery,
  Power,
  PowerOff,
  ShoppingBag,
  Heart,
  Car,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import EdgeReady from '../components/assets/edge_ready.jpg';
import EdgeReady2 from '../components/assets/edge2.jpg';
import EdgeReady3 from '../components/assets/edge3.jpg';
import CommonContactUs from './CommonContactUs';

const EdgeReadyPage = (props) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderImages = [EdgeReady3, EdgeReady2, EdgeReady];

  const goToPrev = () => setCurrentSlide((prev) => (prev === 0 ? sliderImages.length - 1 : prev - 1));
  const goToNext = () => setCurrentSlide((prev) => (prev === sliderImages.length - 1 ? 0 : prev + 1));

  const services = [
    {
      icon: <Wifi className="w-8 h-8 text-blue-600" />,
      title: "Edge Computing",
      description: "Process data closer to the source for faster response times and reduced latency.",
      features: ["Local data processing", "Real-time analytics", "Reduced bandwidth usage", "Offline capabilities"]
    },
    {
      icon: <Server className="w-8 h-8 text-green-600" />,
      title: "IoT Solutions",
      description: "Connect and manage devices at the edge for intelligent automation and monitoring.",
      features: ["Device connectivity", "Sensor integration", "Remote monitoring", "Predictive maintenance"]
    },
    {
      icon: <Signal className="w-8 h-8 text-purple-600" />,
      title: "5G Integration",
      description: "Leverage 5G networks for ultra-fast, low-latency edge computing applications.",
      features: ["Ultra-low latency", "High bandwidth", "Network slicing", "Edge cloud deployment"]
    },
    {
      icon: <Zap className="w-8 h-8 text-orange-600" />,
      title: "Edge Analytics",
      description: "Real-time data analysis at the edge for immediate insights and decision making.",
      features: ["Stream processing", "Machine learning inference", "Anomaly detection", "Predictive analytics"]
    }
  ];

  const benefits = [
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Ultra-Low Latency",
      description: "Process data locally for near-instantaneous response times."
    },
    {
      icon: <DollarSign className="w-8 h-8 text-green-600" />,
      title: "Cost Efficiency",
      description: "Reduce bandwidth costs and cloud processing expenses."
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Enhanced Security",
      description: "Keep sensitive data local and reduce exposure to network threats."
    },
    {
      icon: <Globe className="w-8 h-8 text-orange-600" />,
      title: "Global Reach",
      description: "Deploy applications anywhere with edge computing infrastructure."
    }
  ];

  const edgeTechnologies = [
    {
      name: "Edge Servers",
      description: "High-performance computing nodes deployed at network edges",
      icon: <Server className="w-6 h-6" />
    },
    {
      name: "IoT Gateways",
      description: "Connect and manage IoT devices with edge processing capabilities",
      icon: <Router className="w-6 h-6" />
    },
    {
      name: "Edge Databases",
      description: "Distributed databases optimized for edge computing environments",
      icon: <Database className="w-6 h-6" />
    },
    {
      name: "5G Networks",
      description: "Next-generation wireless networks enabling edge computing",
      icon: <Signal className="w-6 h-6" />
    },
    {
      name: "Edge AI/ML",
      description: "Machine learning models optimized for edge deployment",
      icon: <Cpu className="w-6 h-6" />
    },
    {
      name: "Edge Security",
      description: "Security solutions designed for edge computing environments",
      icon: <Shield className="w-6 h-6" />
    }
  ];

  const useCases = [
    {
      industry: "Manufacturing",
      applications: ["Predictive maintenance", "Quality control", "Supply chain optimization", "Real-time monitoring"],
      icon: <Settings className="w-8 h-8 text-blue-600" />
    },
    {
      industry: "Healthcare",
      applications: ["Remote patient monitoring", "Medical device management", "Telemedicine", "Emergency response"],
      icon: <Activity className="w-8 h-8 text-red-600" />
    },
    {
      industry: "Transportation",
      applications: ["Autonomous vehicles", "Traffic management", "Fleet optimization", "Smart cities"],
      icon: <Car className="w-8 h-8 text-green-600" />
    },
    {
      industry: "Retail",
      applications: ["Inventory management", "Customer analytics", "Smart shelves", "Loss prevention"],
      icon: <ShoppingBag className="w-8 h-8 text-purple-600" />
    }
  ];

  const testimonials = [
    {
      name: "Robert Wilson",
      role: "CTO",
      company: "Smart Manufacturing Corp",
      content: "TechMind's edge computing solution reduced our response times by 90% and improved our production efficiency significantly.",
      rating: 5
    },
    {
      name: "Dr. Lisa Chen",
      role: "VP of Technology",
      company: "Digital Health Systems",
      content: "The edge-ready IoT platform transformed our patient monitoring capabilities, enabling real-time healthcare delivery.",
      rating: 5
    },
    {
      name: "Michael Rodriguez",
      role: "Infrastructure Director",
      company: "Smart City Solutions",
      content: "Their edge computing expertise helped us build a comprehensive smart city infrastructure with minimal latency.",
      rating: 5
    }
  ];

   // Auto-advance slider every 4 seconds
      useEffect(() => {
        const interval = setInterval(() => {
          setCurrentSlide((prev) =>
            prev === sliderImages.length - 1 ? 0 : prev + 1
          );
        }, 8000);
        return () => clearInterval(interval);
      }, [sliderImages.length]);
      const scrollIntoView = () => {
        const element = document.getElementById("learn-more-edge");
        if (element) {
          const yOffset = -80; // adjust based on your header height
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Slider */}
      {props?.from==='Home' ?null:
      <section className="relative bg-[#1A193B] h-[716px] text-white overflow-hidden pt-24">
        <div className="absolute inset-0 bg-black/20 z-10"></div>
        {/* Slider Images */}
        <div className="absolute inset-0 w-full h-full flex transition-all duration-700 z-0">
          {sliderImages.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt="Edge Ready Slide"
              className={`object-cover w-full h-full absolute top-0 left-0 transition-opacity duration-700 ${currentSlide === idx ? 'opacity-70 z-20' : 'opacity-0 z-0'}`}
              style={{ transition: 'opacity 0.7s' }}
            />
          ))}
        </div>
        {/* Slider Controls */}
        <div className="absolute left-0 right-0 flex justify-between items-center h-full px-4 z-30 pointer-events-none">
          <button
            onClick={goToPrev}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Previous Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={goToNext}
            className="bg-black/40 hover:bg-black/60 text-white rounded-full p-2 pointer-events-auto"
            aria-label="Next Slide"
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </div>
        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30" style={{zIndex:'42'}}>
          {sliderImages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`w-3 h-3 rounded-full ${currentSlide === idx ? 'bg-white' : 'bg-white/40'} border border-white transition-all`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
        {/* Hero Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 lg:py-56 z-40">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Edge Ready
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-green-100">
              Deploy applications at the edge for ultra-fast performance and real-time capabilities
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-green-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Get Started
              </Link>
              <Link 
               to="#"
               onClick={scrollIntoView}
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-green-600 transition-colors font-medium"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      }

      {/* Benefits Section */}
      <section id='learn-more-edge' className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Edge Ready Solutions?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Leverage edge computing for superior performance and real-time capabilities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Edge Ready Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive edge computing solutions for modern applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="bg-white rounded-lg shadow-sm border p-8 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-6">
                  {service.icon}
                  <h3 className="text-2xl font-semibold text-gray-900 ml-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 text-lg">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-600">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Edge Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Edge Technologies We Use
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge technologies for building edge-ready applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {edgeTechnologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-100 rounded-lg mr-4">
                    {tech.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{tech.name}</h3>
                </div>
                <p className="text-gray-600">
                  {tech.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Edge Computing Use Cases
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how edge computing is transforming different industries
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 + index * 0.1 }}
                className="bg-white rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center mb-4">
                  {useCase.icon}
                  <h3 className="text-xl font-semibold text-gray-900 ml-3">
                    {useCase.industry}
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {useCase.applications.map((app, appIndex) => (
                    <div key={appIndex} className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {app}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Edge Architecture Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Edge Architecture Patterns
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Proven architectural patterns for building edge-ready applications
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Edge-Cloud Hybrid",
                description: "Distribute workloads between edge devices and cloud infrastructure for optimal performance",
                icon: <Network className="w-8 h-8 text-blue-600" />
              },
              {
                title: "Edge-First Design",
                description: "Design applications to prioritize edge processing with cloud as backup",
                icon: <Wifi className="w-8 h-8 text-green-600" />
              },
              {
                title: "Multi-Edge Deployment",
                description: "Deploy applications across multiple edge locations for redundancy and performance",
                icon: <Globe className="w-8 h-8 text-purple-600" />
              }
            ].map((pattern, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + index * 0.1 }}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-center mb-4">
                  {pattern.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">
                  {pattern.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {pattern.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from clients who have successfully implemented edge computing solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.3 + index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role} at {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {/* <section className="py-16 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Go Edge Ready?
            </h2>
            <p className="text-xl mb-8 text-green-100">
              Transform your applications with edge computing capabilities
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact"
                className="px-8 py-3 bg-white text-green-600 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Start Your Edge Journey
              </Link>
              <Link 
                to="/services"
                className="px-8 py-3 border border-white text-white rounded-md hover:bg-white hover:text-green-600 transition-colors font-medium"
              >
                Explore Edge Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section> */}
      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs/>
      </section>
    </div>
  );
};

export default EdgeReadyPage; 